import { useEffect, useRef, useState } from "react";

interface UseLoadMoreSentinelOptions {
  /** Asked for when the end of the list is near. */
  onNearEnd: () => void;
  /**
   * Off while there is nothing to ask for: no further page, a page already on
   * its way, or one that just failed. Turning it back on re-checks at once, so
   * an end that is still in view asks again.
   */
  enabled: boolean;
  /**
   * How many rows the list holds. Re-checks the end whenever a page lands: a
   * fast response can start and finish inside one batched update, leaving no
   * render where `enabled` was off — and the end may still be in view.
   */
  loadedCount: number;
  /**
   * The element the list scrolls inside. Observed against it rather than the
   * screen, because the screen clips the sentinel to the scroller's edge and
   * the lead distance would never apply. `null` is the screen itself, for a
   * list the page scrolls.
   */
  root: Element | null;
  /** How far ahead of the end to ask. Default 400px, below the scroller only. */
  rootMargin?: string;
}

/**
 * Infinite scroll for a list that is paged: returns a ref to put on a marker
 * under the last row, and calls `onNearEnd` when that marker nears view.
 *
 * A callback ref held in state, not a `useRef`, so the observer attaches when
 * the marker mounts — it only exists once the list has rows.
 */
export function useLoadMoreSentinel({
  onNearEnd,
  enabled,
  loadedCount,
  root,
  rootMargin = "0px 0px 400px 0px",
}: UseLoadMoreSentinelOptions) {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  // The latest callback, without re-observing every time its identity changes.
  const onNearEndRef = useRef(onNearEnd);
  useEffect(() => {
    onNearEndRef.current = onNearEnd;
  });

  useEffect(() => {
    if (!sentinel || !enabled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onNearEndRef.current();
        }
      },
      { root, rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // `loadedCount` is not read: it is here to re-observe when a page lands.
  }, [sentinel, enabled, root, rootMargin, loadedCount]);

  return setSentinel;
}
